<template>
  <nav class="tab-nav" aria-label="Secciones">
    <template v-for="link in navLinks">

      <!-- Tab con dropdown -->
      <div
        v-if="link.children && link.children.length"
        :key="'dd-' + link.name"
        class="tab-nav__dropdown"
      >
        <button
          class="tab-nav__item tab-nav__item--has-dropdown"
          :class="{ 'tab-nav__item--active': isParentActive(link) }"
          @click="link.path && goto(link.path)"
        >
          {{ link.name }}<span class="tab-nav__chevron" aria-hidden="true">▾</span>
        </button>
        <ul class="tab-nav__menu" role="menu">
          <li v-for="child in link.children" :key="child.path" role="none">
            <button
              class="tab-nav__menu-item"
              :class="{ 'tab-nav__menu-item--active': isActive(child.path) }"
              role="menuitem"
              @click="goto(child.path)"
            >
              {{ child.name }}
            </button>
          </li>
        </ul>
      </div>

      <!-- Tab normal -->
      <button
        v-else
        :key="link.path || link.action || link.name"
        class="tab-nav__item"
        :class="{
          'tab-nav__item--active': isActive(link),
          'tab-nav__item--danger': isDanger(link.name)
        }"
        @click="activate(link)"
      >
        <font-awesome-icon
          v-if="isDanger(link.name)"
          icon="trash-alt"
          class="tab-nav__trash"
          aria-hidden="true"
        ></font-awesome-icon>
        <font-awesome-icon
          v-else-if="link.icon"
          :icon="link.icon"
          class="tab-nav__icon"
          aria-hidden="true"
        ></font-awesome-icon>
        {{ link.name }}
      </button>

    </template>
  </nav>
</template>

<script>
export default {
  name: 'TabNav',
  props: ['links'],
  computed: {
    navLinks() {
      return (this.links || []).filter(l => l.path || l.action || (l.children && l.children.length))
    }
  },
  methods: {
    activate(link) {
      if (link && link.action) {
        this.$emit('action', link.action)
        return
      }
      this.goto(link)
    },
    goto(linkOrPath) {
      // Acepta objeto link (con posible flag exact) o string path.
      var link = (typeof linkOrPath === 'string') ? { path: linkOrPath } : linkOrPath
      if (!link || !link.path) return
      // "Ya estás aquí": no re-navegar. Para la ruta base (Resumen, exact) se
      // compara exacto, porque su path es prefijo de todas las sub-rutas.
      var here = link.exact
        ? this.$route.path === link.path
        : this.$route.fullPath.includes(link.path)
      if (here) return
      this.$router.push(link.path)
    },
    isActive(linkOrPath) {
      // Acepta un objeto link (con posible flag exact) o un string path.
      var link = (typeof linkOrPath === 'string') ? { path: linkOrPath } : linkOrPath
      if (!link || !link.path) return false
      // exact: la ruta base del mapa es prefijo de todas las sub-rutas, así que
      // la pestaña "Resumen" solo se marca activa en coincidencia exacta.
      if (link.exact) return this.$route.path === link.path
      return this.$route.fullPath.includes(link.path)
    },
    isParentActive(link) {
      if (link.path && this.$route.path === link.path) return true
      return !!(link.children && link.children.some(c => this.isActive(c.path)))
    },
    isDanger(name) {
      return name.includes('Eliminar')
    }
  }
}
</script>

<style lang="scss" scoped>
.tab-nav {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 0.1rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid var(--color-line);

  &__dropdown {
    position: relative;
    display: inline-flex;
    align-items: stretch;

    &:hover .tab-nav__menu {
      display: block;
    }
  }

  &__menu {
    display: none;
    position: absolute;
    top: calc(100% + 2px);
    left: 0;
    z-index: 100;
    min-width: 180px;
    background: #fff;
    border: 1px solid var(--color-line);
    border-radius: 0 4px 4px 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    padding: 0.25rem 0;
    list-style: none;
    margin: 0;

    @media (prefers-color-scheme: dark) {
      background: #1e2733;
      border-color: var(--color-line);
    }
  }

  &__menu-item {
    display: block;
    width: 100%;
    background: none;
    border: none;
    text-align: left;
    padding: 0.45rem 0.9rem;
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--color-graphite);
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 0.12s ease, color 0.12s ease;

    &:hover {
      background-color: var(--color-mist);
      color: var(--color-navy);
    }

    &--active {
      color: var(--color-orange);
      font-weight: 600;
    }
  }

  &__item {
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    margin-bottom: -1px;
    padding: 0.6rem 0.95rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--color-graphite);
    cursor: pointer;
    border-radius: 6px 6px 0 0;
    line-height: 1.4;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
    white-space: nowrap;

    &:hover:not(.tab-nav__item--active) {
      color: var(--color-navy);
      background-color: var(--color-mist);
    }

    &--active {
      color: var(--color-navy);
      font-weight: 700;
      border-bottom-color: var(--color-orange);
    }

    &--has-dropdown {
      padding-right: 0.75rem;
    }

    // La acción destructiva se separa visualmente de la navegación: botón
    // delimitado a la derecha en vez de una pestaña más
    &--danger {
      align-self: center;
      margin-left: auto;
      color: var(--color-danger);
      border: 1px solid rgba(178, 58, 46, 0.4);
      border-radius: 6px;
      padding: 0.4rem 0.9rem;
      font-weight: 600;
      line-height: 1.2;

      &:hover:not(.tab-nav__item--active) {
        background-color: #fdf2f2;
        border-color: var(--color-danger);
        color: #922b21;
      }

      &.tab-nav__item--active {
        color: var(--color-white);
        background-color: var(--color-danger);
        border-color: var(--color-danger);
      }
    }
  }

  &__trash {
    margin-right: 0.35rem;
    font-size: 0.8rem;
  }

  &__icon {
    margin-right: 0.35rem;
    font-size: 0.8rem;
  }

  &__chevron {
    margin-left: 0.25rem;
    font-size: 0.7rem;
    vertical-align: middle;
    opacity: 0.7;
  }
}
</style>
